import React, { useState, useEffect } from 'react';
import {
  Paper,
  Stack,
  TextField,
  Button,
  Typography,
  Chip,
  Box,
  Alert,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Help as HelpIcon } from '@mui/icons-material';

export default function PromptBuilder({ onPromptCreated, editingPrompt, existingPrompts = [] }) {
  const [promptKey, setPromptKey] = useState('');
  const [systemMessage, setSystemMessage] = useState('You are a helpful AI assistant.');
  const [promptTemplate, setPromptTemplate] = useState('');
  const [variables, setVariables] = useState([{ name: '', description: '', example: '' }]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Load editing prompt data when provided
  useEffect(() => {
    if (editingPrompt) {
      setPromptKey(editingPrompt.key || '');
      
      // Parse the content from the most recent version
      const latestContent = editingPrompt.content || {};
      setSystemMessage(latestContent.system || 'You are a helpful AI assistant.');
      setPromptTemplate(latestContent.template || '');
      
      // Parse variables if they exist
      if (latestContent.variables) {
        const variableNames = latestContent.variables.split(',').map(v => v.trim()).filter(Boolean);
        const variableArray = variableNames.map(name => ({
          name,
          description: '',
          example: ''
        }));
        setVariables(variableArray.length > 0 ? variableArray : [{ name: '', description: '', example: '' }]);
      } else {
        setVariables([{ name: '', description: '', example: '' }]);
      }
    } else {
      // Reset form for new prompt
      setPromptKey('');
      setSystemMessage('You are a helpful AI assistant.');
      setPromptTemplate('');
      setVariables([{ name: '', description: '', example: '' }]);
    }
  }, [editingPrompt]);

  const addVariable = () => {
    setVariables([...variables, { name: '', description: '', example: '' }]);
  };

  const removeVariable = (index) => {
    setVariables(variables.filter((_, i) => i !== index));
  };

  const updateVariable = (index, field, value) => {
    const updated = variables.map((v, i) => 
      i === index ? { ...v, [field]: value } : v
    );
    setVariables(updated);
  };

  const loadTemplate = (template) => {
    switch (template) {
      case 'customer-support':
        setPromptKey('customer-support-bot');
        setSystemMessage('You are a helpful customer support assistant. Be friendly, professional, and try to resolve customer issues efficiently.');
        setPromptTemplate('Hello {{customer_name}}! I\'m here to help with your {{issue_type}}. Can you please provide more details about the problem you\'re experiencing?');
        setVariables([
          { name: 'customer_name', description: 'The customer\'s name', example: 'John Smith' },
          { name: 'issue_type', description: 'Type of issue (billing, technical, etc.)', example: 'billing question' }
        ]);
        break;
      case 'content-writer':
        setPromptKey('content-generator');
        setSystemMessage('You are a skilled content writer who creates engaging, informative content for various purposes.');
        setPromptTemplate('Write a {{content_type}} about {{topic}} for {{audience}}. The tone should be {{tone}} and approximately {{length}} words long.');
        setVariables([
          { name: 'content_type', description: 'Type of content (blog post, email, etc.)', example: 'blog post' },
          { name: 'topic', description: 'Main topic or subject', example: 'sustainable energy' },
          { name: 'audience', description: 'Target audience', example: 'general consumers' },
          { name: 'tone', description: 'Writing tone', example: 'professional but approachable' },
          { name: 'length', description: 'Approximate word count', example: '500' }
        ]);
        break;
      case 'data-analyzer':
        setPromptKey('data-analysis-assistant');
        setSystemMessage('You are a data analysis expert who helps interpret data and provide actionable insights.');
        setPromptTemplate('Analyze the following {{data_type}} data: {{data}}. Focus on {{analysis_focus}} and provide key insights and recommendations.');
        setVariables([
          { name: 'data_type', description: 'Type of data being analyzed', example: 'sales performance' },
          { name: 'data', description: 'The actual data or summary', example: 'Q3 sales increased 15% over Q2' },
          { name: 'analysis_focus', description: 'What to focus the analysis on', example: 'trends and growth opportunities' }
        ]);
        break;
    }
  };

  const createPrompt = async () => {
    if (!promptKey.trim()) {
      setError('Please enter a prompt key (name)');
      return;
    }
    if (!promptTemplate.trim()) {
      setError('Please enter a prompt template');
      return;
    }

    // Validate variables in template
    const templateVars = (promptTemplate.match(/\{\{(\w+)\}\}/g) || []).map(v => v.slice(2, -2));
    const definedVars = variables.filter(v => v.name.trim()).map(v => v.name.trim());
    const missingVars = templateVars.filter(v => !definedVars.includes(v));
    
    if (missingVars.length > 0) {
      setError(`Template uses variables that aren't defined: ${missingVars.join(', ')}`);
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Build the content object in the format the backend expects
      const content = {
        system: systemMessage,
        template: promptTemplate,
        variables: variables
          .filter(v => v.name.trim())
          .reduce((acc, v) => {
            acc[v.name.trim()] = {
              description: v.description || '',
              example: v.example || ''
            };
            return acc;
          }, {})
      };

      const response = await fetch(`/api/lock/prompts/${promptKey}/versions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ content })
      });

      if (!response.ok) {
        throw new Error(`Failed to create prompt: ${response.statusText}`);
      }

      const result = await response.json();
      
      // Reset form
      setPromptKey('');
      setSystemMessage('You are a helpful AI assistant.');
      setPromptTemplate('');
      setVariables([{ name: '', description: '', example: '' }]);
      
      if (onPromptCreated) {
        onPromptCreated(result);
      }
      
    } catch (err) {
      console.error('Failed to create prompt:', err);
      setError(err.message || 'Failed to create prompt');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Stack spacing={3}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          {editingPrompt ? `Edit Prompt: ${editingPrompt.key}` : 'Create New Prompt'}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {editingPrompt 
            ? 'Update your AI prompt template and variables. Changes will create a new version.'
            : 'Build AI prompts with templates and variables. Use {{variable_name}} syntax for dynamic content.'
          }
        </Typography>

        {/* Quick Templates */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Quick Start Templates
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
            <Button variant="outlined" size="small" onClick={() => loadTemplate('customer-support')}>
              Customer Support
            </Button>
            <Button variant="outlined" size="small" onClick={() => loadTemplate('content-writer')}>
              Content Writer
            </Button>
            <Button variant="outlined" size="small" onClick={() => loadTemplate('data-analyzer')}>
              Data Analyzer
            </Button>
          </Stack>
        </Box>

        <Divider sx={{ my: 3 }} />

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Basic Fields */}
        <Stack spacing={3}>
          <TextField
            fullWidth
            label="Prompt Name"
            value={promptKey}
            onChange={(e) => setPromptKey(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
            helperText={editingPrompt 
              ? "Prompt name cannot be changed when editing (will create new version)" 
              : "A unique identifier for this prompt (lowercase, hyphens only)"
            }
            placeholder="e.g. customer-support-bot"
            disabled={!!editingPrompt}
          />

          <TextField
            fullWidth
            label="System Message"
            value={systemMessage}
            onChange={(e) => setSystemMessage(e.target.value)}
            multiline
            rows={2}
            helperText="Instructions for how the AI should behave"
            placeholder="You are a helpful AI assistant that..."
          />

          <TextField
            fullWidth
            label="Prompt Template"
            value={promptTemplate}
            onChange={(e) => setPromptTemplate(e.target.value)}
            multiline
            rows={4}
            helperText="Your prompt with {{variable}} placeholders for dynamic content"
            placeholder="Hello {{name}}! I can help you with {{task}}..."
          />

          {/* Variables Section */}
          <Box>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <Typography variant="subtitle1">Variables</Typography>
              <Tooltip title="Define variables that can be filled in when using this prompt">
                <IconButton size="small">
                  <HelpIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
            
            {variables.map((variable, index) => (
              <Paper key={index} sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                <Stack direction="row" spacing={2} alignItems="flex-start">
                  <TextField
                    label="Variable Name"
                    value={variable.name}
                    onChange={(e) => updateVariable(index, 'name', e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'))}
                    size="small"
                    sx={{ minWidth: 150 }}
                    placeholder="variable_name"
                  />
                  <TextField
                    label="Description"
                    value={variable.description}
                    onChange={(e) => updateVariable(index, 'description', e.target.value)}
                    size="small"
                    fullWidth
                    placeholder="What this variable represents"
                  />
                  <TextField
                    label="Example"
                    value={variable.example}
                    onChange={(e) => updateVariable(index, 'example', e.target.value)}
                    size="small"
                    sx={{ minWidth: 150 }}
                    placeholder="Sample value"
                  />
                  <IconButton 
                    onClick={() => removeVariable(index)} 
                    disabled={variables.length === 1}
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Stack>
              </Paper>
            ))}
            
            <Button
              startIcon={<AddIcon />}
              onClick={addVariable}
              variant="outlined"
              size="small"
            >
              Add Variable
            </Button>
          </Box>

          {/* Preview */}
          {promptTemplate && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Preview
              </Typography>
              <Paper sx={{ p: 2, bgcolor: 'grey.50', fontFamily: 'monospace', fontSize: '0.9em' }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                  System: {systemMessage}
                </Typography>
                <Typography variant="body2">
                  User: {promptTemplate}
                </Typography>
              </Paper>
            </Box>
          )}

          {/* Actions */}
          <Stack direction="row" spacing={2}>
            <Button
              variant="contained"
              onClick={createPrompt}
              disabled={loading || !promptKey.trim() || !promptTemplate.trim()}
              size="large"
            >
              {loading 
                ? (editingPrompt ? 'Updating...' : 'Creating...') 
                : (editingPrompt ? 'Update Prompt' : 'Create Prompt')
              }
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </Stack>
  );
}